import React from 'react';
import { useSelector } from 'react-redux';
import { Spacing, Text, Icon, Link } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import PoweredBy from 'components/PoweredBy';
import ExchangeFormSlides from './ExchangeFormSlides';

const propTypes = {
  /** Content for #left-content (forms, Formik, etc.). */
  children: PropTypes.node.isRequired,
  /** Informative copy for the footer. Supplied by whoever renders the form; omitted, the area stays empty. */
  disclaimer: PropTypes.node,
  /** Artwork for #right-content, replacing the slides. Callers pass their own asset; the layout holds no brand map. */
  rightContentBannerSrc: PropTypes.string,
  slidesTicketCaptionKey: PropTypes.string,
};

/**
 * ExchangeFormLayout component.
 * Two-column shell for the redesigned exchange flow: #left-content holds the form, #right-content
 * holds optional artwork, and the footer carries the disclaimer, customer-service contacts and
 * PoweredBy.
 *
 * Artwork and disclaimer arrive as props rather than being resolved inside, so adding a brand or a
 * new flow needs no edit here.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Content for #left-content.
 * @param {React.ReactNode} [props.disclaimer] - Informative copy for the footer; none leaves it empty.
 * @param {string} [props.rightContentBannerSrc] - Artwork for #right-content.
 * @param {string} [props.slidesTicketCaptionKey] - Copy key for the panel's first caption, so the
 *   panel names the data the rendered form actually asks for.
 * @returns {JSX.Element}
 */
const ExchangeFormLayout = ({
  children,
  disclaimer = null,
  rightContentBannerSrc = null,
  slidesTicketCaptionKey = undefined,
}) => {
  const { copies } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation(['exchange', 'general']);

  const providerPhones = copies?.provider?.phones || [];
  const phoneItems = providerPhones.map((phone) => (
    <div key={phone.value} className="purchase-sidebar-contact-item">
      <Link text={phone.display} href={`tel:${phone.value}`} type="accent" />
      {phone.label && (
        <Text elementType="span" size="S" color="grayLight">{` ${phone.label}`}</Text>
      )}
    </div>
  ));

  const hasNoPhones = phoneItems.length === 0;
  const contactItems =
    hasNoPhones && copies?.provider?.email ? (
      <div className="purchase-sidebar-contact-item flex items-center gap-2">
        <Icon type="Email" size="S" />
        <Link text={copies.provider.email} href={`mailto:${copies.provider.email}`} type="accent" />
      </div>
    ) : (
      phoneItems
    );

  return (
    <div id="exchange-form" className="gap-4 flex flex-col">
      <div
        id="exchange-form-content"
        className="-mt-[15px] mx-[calc(50%_-_50vw)] w-screen flex flex-col overflow-hidden rounded-none md:mt-0 md:mx-0 md:w-auto md:rounded-xl md:flex-row md:items-stretch min-h-[500px]"
      >
        <div
          id="left-content"
          className="-mt-5 flex w-full flex-col gap-4 self-stretch rounded-t-2xl bg-white py-6 px-6 md:mt-0 md:rounded-none md:py-11 md:px-16 md:min-h-0 md:flex-1 md:basis-0 md:min-w-0"
        >
          {children}
        </div>
        {/* Above the form on a phone, beside it from `md` up. It leads rather than follows because
            it answers the question the first field asks — where the number it wants comes from —
            and a panel below the submit would answer it after the traveller had to guess. */}
        <div
          id="right-content"
          className="order-first flex flex-none basis-auto flex-col items-center justify-center self-stretch bg-primary-regular md:order-none md:min-h-0 md:min-w-0 md:flex-1 md:basis-0"
        >
          {/* A caller with its own artwork gets exactly that; everyone else gets the slides, which
              answer where the numbers the form asks for can be found. */}
          {rightContentBannerSrc ? (
            <img
              className="h-auto w-auto max-w-[300px] object-contain object-center"
              src={rightContentBannerSrc}
              alt=""
            />
          ) : (
            <ExchangeFormSlides ticketCaptionKey={slidesTicketCaptionKey} />
          )}
        </div>
      </div>

      <div id="exchange-form-footer" className="gap-4 flex flex-col mt-8 justify-between">
        <Spacing justifyContent="space-between" isResponsive>
          {/* The informative area is the same on every brand: it is always here, so the form above
              it keeps the same height whoever is rendering. What fills it is the rendered form's
              own copy — a brand with no rules of its own leaves it empty rather than inheriting
              another brand's, which is what the shared default used to do. */}
          <div className="gap-2 flex flex-row items-start">
            {Boolean(disclaimer) && (
              <Icon type="RedeemTicket" size="L" color="primary" className="flex-shrink-0" />
            )}
            {disclaimer}
          </div>
          <div id="customer-service" className="flex flex-col items-start gap-2 md:items-center">
            <Text weight="semibold" size="S">
              {t('general:customer_service')}
            </Text>
            <Spacing vertical size="XS">
              {contactItems}
            </Spacing>
          </div>
        </Spacing>
      </div>

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-10 flex justify-end p-4">
        <div className="pointer-events-auto">
          <PoweredBy />
        </div>
      </div>
    </div>
  );
};

ExchangeFormLayout.propTypes = propTypes;

export default ExchangeFormLayout;
